import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { NavItem } from './NavItem';
import { activeEntry } from './spaces';
import { useCurrentSpace } from './use-current-space';
import { useNavBadges } from './use-nav-badges';

/** Sidebar of the Control Center (≥ 1100 px): the entries of the open space only. The PCA has
 *  big rows without groups and the counters of the operation; the Segreteria is dense, in the
 *  order of the work. Services and connection are in the status bar. */
export function Nav() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { current } = useCurrentSpace();
  const pca = current?.key === 'pca';
  const badges = useNavBadges(pca);
  if (current === null) return null;
  const active = activeEntry(pathname, current.entries);

  return (
    <nav
      className={['nav', pca ? 'pca' : ''].join(' ').trim()}
      aria-label={t('shell.nav.label', { space: t(`shell.spaces.${current.key}`) })}
    >
      {current.groups.map((group) => (
        <div key={group.key ?? 'all'} style={{ display: 'contents' }}>
          {group.key === null ? null : (
            <p className="nav-h">{t(`shell.nav.groups.${group.key}`)}</p>
          )}
          {group.entries.map((entry) => (
            <NavItem
              key={entry.key}
              entry={entry}
              active={active?.key === entry.key}
              large={pca}
              badge={entry.badge === undefined ? undefined : badges[entry.badge]}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

/** Compact module rail below 1100 px: the entries of the open space, the switch stays in the
 *  topbar. */
export function ModuleRail() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { current } = useCurrentSpace();
  if (current === null) return null;
  const active = activeEntry(pathname, current.entries);
  return (
    <nav className="railm" aria-label={t('shell.nav.modulesRail')}>
      {current.entries.map((entry) => (
        <Link
          key={entry.key}
          to={entry.to}
          activeOptions={{ exact: true }}
          activeProps={{}}
          aria-current={active?.key === entry.key ? 'page' : undefined}
        >
          {t(`shell.nav.entries.${entry.key}`)}
        </Link>
      ))}
    </nav>
  );
}
